import capitalize from 'lodash/capitalize';

import { Badge, Box, Button, Flex, Text } from '@vimeo/bokeh';
import { Checkmark, CloseX } from '@vimeo/bokeh-icons';
import { getCurrentLocale, translate } from '@vimeo/i18n-runtime';
import { buildCheckoutUrl, type PaywallTrackingProps } from '@vimeo/picox';
import { useGetSubscriptionPlansData } from '@vimeo/subscription-api-plans-data-hook';

import {
  ComparisonPaywallHeader,
  ComparisonTableRowDesktop,
  OtherPlansButton,
} from '../components';
import { getComparisonFeatures } from './comparisonFeatures';

interface TableComparisonPaywallProps {
  tier: 'starter' | 'standard' | 'advanced';
  currentTier?: 'free' | 'starter' | 'standard' | 'advanced';
  paywallTracking: PaywallTrackingProps;
  isUserEligibleForFreeTrial?: boolean;
}

export function TableComparisonPaywall({
  tier,
  currentTier,
  paywallTracking,
  isUserEligibleForFreeTrial = false,
}: TableComparisonPaywallProps) {
  const plans = useGetSubscriptionPlansData(
    ['free', 'starter', 'standard', 'advanced'],
    undefined,
    false,
  );
  const currentPlan =
    plans?.find((plan) => plan.tier === currentTier) ??
    plans?.find((plan) => plan.metadata?.interactions?.purchase?.status === 'purchased');
  const targetPlan =
    plans?.find((plan) => plan.tier === tier) ??
    plans?.find(
      (plan) => plan.tier === 'starter' || plan.tier === 'standard' || plan.tier === 'advanced',
    );
  const hasTrialPath = Boolean(targetPlan?.metadata?.interactions?.purchase?.uri?.freeTrial);
  const isTrial = hasTrialPath && isUserEligibleForFreeTrial;

  const isLoaded = Boolean(targetPlan);
  const currency =
    targetPlan?.currency?.currencyCode ?? currentPlan?.currency?.currencyCode ?? 'USD';
  const currentPrice =
    currentPlan?.tier === 'free'
      ? formatCurrencyAmount(0, currency)
      : (currentPlan?.metadata?.purchasedProduct?.displayPriceFormatted ??
        currentPlan?.priceFormatted?.annualMonthly ??
        '');
  const targetPrice = targetPlan?.priceFormatted?.annualMonthly ?? '';
  const targetDisplayName = targetPlan?.name ?? capitalize(`${tier}`);
  const currentDisplayName = currentPlan?.name ?? '';
  const features = getComparisonFeatures({
    currentPlan,
    targetPlan,
    currentTier: currentPlan?.tier,
    targetTier: tier,
  });

  const handleCtaClick = () => {
    if (typeof window === 'undefined') {
      return;
    }
    const checkoutPath = buildCheckoutUrl({
      ...paywallTracking,
      tier,
      isTrial,
    });
    window.location.assign(checkoutPath);
  };

  const ctaLabel = isTrial
    ? translate('Start free trial')
    : translate({
        singular: 'Join Vimeo {PLAN_NAME}',
        replacements: { PLAN_NAME: targetDisplayName },
      });

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      gap="24px"
      pt={{ base: '24px', md: '0' }}
      pb="24px"
      mx="auto"
    >
      <ComparisonPaywallHeader tierName={targetDisplayName} />

      {/* Desktop: single CSS grid so header + rows share column tracks */}
      <Box
        display={{ base: 'none', md: 'grid' }}
        gridTemplateColumns="1fr 1fr 1fr"
        gap="0 8px"
        width="100%"
      >
        {/* Row 1: Header */}
        {/* Feature label column — empty spacer */}
        <Box />

        {/* Current column header */}
        <Flex
          direction="column"
          gap="md"
          alignItems="start"
          justifyContent="end"
          opacity={0.7}
          pb="md"
          pt="2xl"
          px="md"
          borderTopLeftRadius="2xl"
          borderTopRightRadius="2xl"
          borderTopWidth="2px"
          borderLeftWidth="2px"
          borderRightWidth="2px"
          borderStyle="solid"
          borderColor="stroke"
          position="relative"
        >
          <Box position="absolute" left="14px" top="11px">
            <Badge variant="secondary" size="sm">
              {translate('Your plan')}
            </Badge>
          </Box>
          <Text variant="heading-md" letterSpacing="-0.04em" width="100%">
            {currentDisplayName}
          </Text>
          <Flex direction="column" height="72px" pb="8px" width="100%">
            <Text variant="heading-lg" letterSpacing="-0.04em">
              {currentPrice}
            </Text>
          </Flex>
          <Box height="40px" opacity={0} width="100%">
            <Button size="md" variant="tertiary" width="100%" isDisabled>
              {translate('Sign up')}
            </Button>
          </Box>
        </Flex>

        {/* Target column header */}
        <Flex
          direction="column"
          gap="md"
          alignItems="start"
          justifyContent="end"
          pb="md"
          pt="2xl"
          px="md"
          borderTopLeftRadius="2xl"
          borderTopRightRadius="2xl"
          bg="background"
          position="relative"
        >
          <Box position="absolute" left="16px" top="12px">
            <Badge variant="upgrade" size="sm">
              {translate('Upgrade')}
            </Badge>
          </Box>
          <Text variant="heading-md" letterSpacing="-0.04em" width="100%">
            {targetDisplayName}
          </Text>
          <Flex direction="column" height="72px" pb="8px" lineHeight="1.2" width="100%">
            <Text variant="heading-lg" letterSpacing="-0.04em">
              {targetPrice}
            </Text>
            <Flex direction="column" fontSize="body-sm" color="text-secondary" lineHeight="1.2">
              <Text fontSize="body-sm" color="text-secondary">
                {translate('per month')}
              </Text>
              <Text fontSize="body-sm" color="text-secondary">
                {translate('billed annually')}
              </Text>
            </Flex>
          </Flex>
          <Button
            size="md"
            variant="primary"
            width="100%"
            onClick={handleCtaClick}
            isDisabled={!isLoaded}
          >
            {ctaLabel}
          </Button>
        </Flex>

        {/* Feature rows — each renders 3 grid cells */}
        {features.map((feature, idx) => (
          <ComparisonTableRowDesktop
            key={feature.name}
            featureName={feature.name}
            tooltip={feature.tooltip}
            currentValue={feature.currentValue}
            targetValue={feature.targetValue}
            isLastRow={idx === features.length - 1}
          />
        ))}
      </Box>

      {/* Mobile: two side-by-side column blocks */}
      <Flex display={{ base: 'flex', md: 'none' }} gap="sm" width="100%" alignItems="stretch">
        {/* Current column */}
        <Flex
          flex="1"
          direction="column"
          borderWidth="2px"
          borderStyle="solid"
          borderColor="stroke"
          borderRadius="2xl"
          opacity={0.7}
          overflow="hidden"
          pb="md"
        >
          {/* Plan header */}
          <Flex direction="column" gap="xs" p="md" position="relative">
            <Box position="absolute" left="12px" top="10px">
              <Badge variant="secondary" size="sm">
                {translate('Your plan')}
              </Badge>
            </Box>
            <Box pt="24px">
              <Text variant="heading-sm">{currentDisplayName}</Text>
            </Box>
            <Text variant="heading-lg" letterSpacing="-0.04em">
              {currentPrice}
            </Text>
          </Flex>

          {/* Spacer pushes features to the bottom to align with target column */}
          <Box flex="1" />

          {/* Feature values */}
          {features.map((feature) => (
            <Flex key={feature.name} direction="column" gap="2px" px="md" py="sm">
              <Text variant="heading-2xs" color="text-secondary">
                {feature.name}
              </Text>
              <MobileCellContent value={feature.currentValue} />
            </Flex>
          ))}
        </Flex>

        {/* Target column */}
        <Flex
          flex="1"
          direction="column"
          bg="background"
          borderRadius="2xl"
          overflow="hidden"
          pb="md"
        >
          {/* Plan header */}
          <Flex direction="column" gap="xs" p="md" position="relative">
            <Box position="absolute" left="12px" top="10px">
              <Badge variant="upgrade" size="sm">
                {translate('Recommended')}
              </Badge>
            </Box>
            <Box pt="24px">
              <Text variant="heading-sm">{targetDisplayName}</Text>
            </Box>
            <Flex direction="column" pb="sm">
              <Text variant="heading-lg" letterSpacing="-0.04em">
                {targetPrice}
              </Text>
              <Text fontSize="12px" color="text-secondary">
                {translate('per month, billed annually')}
              </Text>
            </Flex>
            <Button
              size="md"
              variant="primary"
              width="100%"
              whiteSpace="normal"
              onClick={handleCtaClick}
              isDisabled={!isLoaded}
            >
              {ctaLabel}
            </Button>
          </Flex>

          {/* Feature values */}
          {features.map((feature) => (
            <Flex key={feature.name} direction="column" gap="2px" px="md" py="sm">
              <Text variant="heading-2xs" color="text-secondary">
                {feature.name}
              </Text>
              <MobileCellContent value={feature.targetValue} />
            </Flex>
          ))}
        </Flex>
      </Flex>

      <Flex width="100%" maxWidth="400px" mx="auto">
        <OtherPlansButton paywallTracking={paywallTracking} />
      </Flex>
    </Flex>
  );
}

function MobileCellContent({ value }: { value: string | boolean }) {
  if (typeof value === 'boolean') {
    if (value) {
      return <Checkmark width={16} height={16} />;
    }
    return <CloseX width={16} height={16} color="#9CA3AF" opacity={0.6} />;
  }

  return (
    <Text fontSize="body-md" lineHeight="1.2">
      {value}
    </Text>
  );
}

function formatCurrencyAmount(amount: number, currencyCode: string): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
